/**
 * Read-through cache for data the user may need to consult offline.
 * Stored in IndexedDB, scoped by userId. Separate database from the
 * mutation queue (offlineQueue.ts) to avoid schema conflicts.
 *
 * Scope v1: today + tomorrow sessions for the authenticated user.
 */
import { openDB, type IDBPDatabase } from 'idb'
import { supabase } from '@/lib/supabase'
import { transformBooking } from '@/utils/transforms'
import { trackConversion } from '@/utils/analytics'
import type { Booking } from '@/types'

const DB_NAME = 'antiplanning-cache'
const DB_VERSION = 1
const STORE_KV = 'kv'

let dbPromise: Promise<IDBPDatabase> | null = null

function db() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_KV)) {
          db.createObjectStore(STORE_KV) // generic key-value, external key
        }
      },
    })
  }
  return dbPromise
}

function keyFor(kind: string, userId: string): string {
  return `${kind}:${userId}`
}

interface CachedBookings {
  userId: string
  centerId: string
  cachedAt: string
  dateFrom: string
  dateTo: string
  bookings: Booking[]
}

/**
 * Prefetches the user's visible sessions for today + tomorrow.
 * Intended to be called right after login and on demand.
 */
export async function prefetchBookings(userId: string, centerId: string, role?: string): Promise<number> {
  if (!userId || !centerId) return 0
  try {
    const now = new Date()
    const from = new Date(now); from.setHours(0, 0, 0, 0)
    const to = new Date(now); to.setDate(to.getDate() + 2); to.setHours(0, 0, 0, 0)

    let query = supabase
      .from('training_sessions')
      .select(`
        *,
        room:rooms(id, name, room_type, capacity, building_id, building:buildings(id, name)),
        trainer:profiles!training_sessions_trainer_id_fkey(id, full_name, email),
        subject:subjects(id, name, color),
        class_:classes(id, name, diploma:diplomas(id, title)),
        diploma:diplomas!training_sessions_diploma_id_fkey(id, title)
      `)
      .eq('center_id', centerId)
      .gte('start_time', from.toISOString())
      .lt('start_time', to.toISOString())
      .order('start_time', { ascending: true })

    // For teachers, restrict to their own sessions to keep payload small
    if (role === 'teacher' || role === 'trainer') {
      query = query.eq('trainer_id', userId)
    }

    const { data, error } = await query
    if (error) {
      console.warn('[offlineCache] prefetchBookings failed', error)
      return 0
    }
    const bookings = (data || []).map(transformBooking)
    const payload: CachedBookings = {
      userId,
      centerId,
      cachedAt: new Date().toISOString(),
      dateFrom: from.toISOString(),
      dateTo: to.toISOString(),
      bookings,
    }
    const idb = await db()
    await idb.put(STORE_KV, payload, keyFor('bookings', userId))
    try { trackConversion('offline_prefetch_completed', { value: bookings.length }) } catch {}
    return bookings.length
  } catch (err) {
    console.warn('[offlineCache] prefetchBookings error', err)
    return 0
  }
}

export async function getCachedBookings(userId: string): Promise<CachedBookings | null> {
  try {
    const idb = await db()
    const data = await idb.get(STORE_KV, keyFor('bookings', userId)) as CachedBookings | undefined
    return data || null
  } catch {
    return null
  }
}

export async function clearCacheFor(userId: string): Promise<void> {
  try {
    const idb = await db()
    const keys = await idb.getAllKeys(STORE_KV)
    for (const k of keys) {
      if (typeof k === 'string' && k.endsWith(`:${userId}`)) {
        await idb.delete(STORE_KV, k)
      }
    }
  } catch {
    // best-effort
  }
}
